import type {
  CurrencyLabels,
  CountryCodesAbbreviationsType,
  CurrencyCodeKeyTypes,
  CurrencySymbolTypes,
  CurrencyCodeValueTypes,
  CurrencyDelimiters,
} from '@axiom/const';
import {
  CurrencyLocaleMap,
  CountryCodesConst,
  CurrencyMap,
} from '@axiom/const';

const {
  codes,
  currencySymbols,
  currenciesWithLabel,
  currenciesDelimitersByCurrencyCode,
} = CurrencyMap;
const { CountryCodesAbbreviations } = CountryCodesConst;

const countryToCurrency = {
  [CountryCodesAbbreviations.AU]: codes.AUD,
  [CountryCodesAbbreviations.CA]: codes.CAD,
  [CountryCodesAbbreviations.CH]: codes.CHF,
  [CountryCodesAbbreviations.SG]: codes.SGD,
  [CountryCodesAbbreviations.DE]: codes.EUR,
  [CountryCodesAbbreviations.HK]: codes.HKD,
  [CountryCodesAbbreviations.UK]: codes.GBP,
  [CountryCodesAbbreviations.US]: codes.USD,
};

const currencyToCountry = Object.entries(countryToCurrency).reduce(
  // @ts-expect-error Needs new type
  (
    acc,
    [countryCode, currencyCode]: [
      CountryCodesAbbreviationsType,
      CurrencyCodeKeyTypes,
    ]
  ): Record<CurrencyCodeKeyTypes, CountryCodesAbbreviationsType> => {
    acc[currencyCode] = countryCode;
    return acc;
  },
  {} as Record<CurrencyCodeKeyTypes, CountryCodesAbbreviationsType>
);

export const CurrencyUtil = {
  convertToSymbol: (
    code?: CurrencyCodeKeyTypes | null
  ): CurrencySymbolTypes | null => {
    if (!code) {
      return null;
    }
    return currencySymbols[code];
  },

  getCountryCodeByCurrency: (
    currency: CurrencyCodeKeyTypes
  ): CountryCodesAbbreviationsType => {
    // @ts-expect-error Needs new type
    return currencyToCountry[currency] ?? null;
  },

  getCurrencyWithLabel: (
    code?: CurrencyCodeKeyTypes
  ): CurrencyLabels | null => {
    if (!code) {
      return null;
    }
    return currenciesWithLabel[code];
  },

  getCurrencyWithLabelByCountryCode: (
    countryCode: CountryCodesAbbreviationsType = CountryCodesAbbreviations.US
  ): CurrencyLabels | null => {
    if (!countryToCurrency[countryCode]) {
      return null;
    }
    return currenciesWithLabel[countryToCurrency[countryCode]];
  },

  getCurrencyCodeByCountryCode: (
    countryCode: CountryCodesAbbreviationsType = CountryCodesAbbreviations.US
  ): CurrencyCodeKeyTypes | null => {
    return countryToCurrency[countryCode] ?? null;
  },

  getFormattedCurrency: ({
    value,
    currencyCode,
    removeCentsIfZero = true,
    currencyDisplay = 'symbol',
  }: {
    value?: number | string | null;
    currencyCode?: CurrencyCodeValueTypes | null;
    removeCentsIfZero?: boolean;
    currencyDisplay?: 'symbol' | 'code';
  }): string | null => {
    if (!value) {
      return null;
    }

    currencyCode = currencyCode || codes.USD;

    const delimiter: CurrencyDelimiters =
      currenciesDelimitersByCurrencyCode[currencyCode];
    let currency = `${value}`;

    // Already formatted === START
    const delimiterPattern = new RegExp(delimiter, 'g');
    const countDelimiters = (currency.match(delimiterPattern) || []).length;
    if (countDelimiters >= 1) return currency;
    // Already formatted === END

    // Remove currency symbols ($, AU$, etc) === START
    currency = Object.values(currencySymbols).reduce((str, symbol) => {
      const hasSymbol = str.includes(symbol);
      return hasSymbol ? str.substring(symbol.length).trim() : str;
    }, currency);
    // Remove currency symbols ($, AU$, etc) === END

    // Format the currency string === START
    const numericValue = parseFloat(currency);
    const stripCents = removeCentsIfZero && numericValue % 1 === 0;

    currency = numericValue.toLocaleString(CurrencyLocaleMap[currencyCode], {
      style: 'currency',
      currency: currencyCode,
      currencyDisplay,
      minimumFractionDigits: stripCents ? 0 : 2,
      maximumFractionDigits: 2,
    });
    // Format the currency string === END

    /**
     * This is what this function returns
     *   ┌──────┬─────────────────────────┬────────────────────────────┐
     *   │ Code │ currencyDisplay: 'code' │ currencyDisplay: 'symbol'  │
     *   ├──────┼─────────────────────────┼────────────────────────────┤
     *   │ USD  │ "USD 1,234.56"          │ "$1,234.56"                │
     *   │ GBP  │ "GBP 1,234.56"          │ "£1,234.56"                │
     *   │ AUD  │ "AUD 1,234.56"          │ "$1,234.56"                │
     *   │ HKD  │ "HKD 1,234.56"          │ "HK$1,234.56"              │
     *   │ SGD  │ "SGD 1,234.56"          │ "$1,234.56"                │
     *   │ EUR  │ "1.234,56 EUR"          │ "1.234,56 €"               │
     *   │ CAD  │ "1 234,56 CAD"          │ "1 234,56 $"               │
     *   │ CHF  │ "CHF 1’234.56"          │ "CHF 1’234.56"             │
     *   └──────┴─────────────────────────┴────────────────────────────┘
     * */

    return currency;
  },
};
